import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Icon,
  Alert,
  Upload,
  Tabs,
  Select,
  Input,
  InputNumber,
  Tooltip,
  message,
  Progress
} from 'antd'
import VoiceUploadDetailsModal from '../VoiceUploadDetailsModal'
import VoiceDetailsModal from './components/VoiceDetailsModal'
import VoiceLibrarySection from './components/VoiceLibrarySection'
import VoiceListItem from './components/VoiceListItem'
import { LANGUAGE_OPTIONS } from '../../../constants/videoLanguages'
import { getVoiceDisplayName, getVoicePreviewUrl } from './utils'
import './VoiceSelectionModal.scss'

const { TabPane } = Tabs
const { Option } = Select
const TABLE_LAYOUT_VARIANTS = new Set(['system', 'custom'])
const SYNTHESIS_LANGUAGE_OPTIONS = [
  'Arabic',
  'Burmese',
  'Chinese',
  'Danish',
  'Dutch',
  'English',
  'Finnish',
  'French',
  'German',
  'Greek',
  'Hebrew',
  'Hindi',
  'Indonesian',
  'Italian',
  'Japanese',
  'Khmer',
  'Korean',
  'Lao',
  'Malay',
  'Norwegian',
  'Polish',
  'Portuguese',
  'Russian',
  'Spanish',
  'Swahili',
  'Swedish',
  'Tagalog',
  'Thai',
  'Turkish',
  'Vietnamese'
]
const GENDER_OPTIONS = ['female', 'male']
const AGE_OPTIONS = [
  'child',
  'teenager',
  'young adult',
  'mid-20s',
  'mid-30s',
  'middle-aged',
  'mature',
  'elderly'
]
const EMOTION_OPTIONS = [
  'neutral',
  'warm',
  'calm',
  'gentle',
  'cheerful',
  'excited',
  'enthusiastic',
  'serious',
  'confident',
  'dramatic'
]
const PACE_OPTIONS = [
  'very slow',
  'slow',
  'moderate',
  'natural',
  'slightly fast',
  'fast',
  'measured and steady'
]
const formatOptionLabel = value => {
  if (typeof value !== 'string') return ''
  const normalized = value.trim()
  if (!normalized) return ''
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

const isTableLayoutVariant = variant => TABLE_LAYOUT_VARIANTS.has(variant)

const getTableColumns = variant => {
  if (variant === 'system') return ['Voice', 'Gender', 'Actions']
  if (variant === 'custom') return ['Voice', 'Gender', 'Actions']
  return ['Voice', 'Profile', 'Actions']
}

const VoiceSelectionModal = ({
  visible,
  onCancel,
  onSelect,
  voices,
  loadingVoices,
  handlePlayPreview,
  playingVoice,
  audioProgress,
  selectedVoiceId,
  targetName,
  handleUploadVoice,
  uploading,
  uploadProgress,
  uploadTaskProgress = 0,
  uploadTaskStatus = null,
  uploadTaskMessage = '',
  uploadError,
  setUploadError,
  beforeUpload,
  fetchVoices,
  pendingUploadFile,
  setPendingUploadFile,
  pendingVoiceName,
  pendingVoiceDescription,
  pendingVoiceLanguage,
  setPendingVoiceName,
  setPendingVoiceDescription,
  setPendingVoiceLanguage,
  onRetryUpload,
  onOverwriteUpload,
  recommendedVoices = [],
  loadingRecommendedVoices = false,
  recommendedVoicesError = null,
  voicesError = null,
  showRecommendedTab = false,
  fetchRecommendedVoices,
  onSynthesizeVoice,
  synthesizeTaskProgress = 0,
  synthesizeTaskStatus = null,
  synthesizeTaskMessage = '',
  canDeleteVoice,
  onDeleteVoice
}) => {
  const [activeTab, setActiveTab] = useState(
    showRecommendedTab ? 'recommended' : 'system'
  )
  const [detailsView, setDetailsView] = useState(null)
  const [isCreateVoiceModalVisible, setIsCreateVoiceModalVisible] = useState(
    false
  )
  const [isSynthesisSubmitting, setIsSynthesisSubmitting] = useState(false)
  const [synthesizedPreview, setSynthesizedPreview] = useState(null)
  const [isUploadDetailsModalVisible, setUploadDetailsModalVisible] = useState(
    false
  )
  const [synthesisForm, setSynthesisForm] = useState({
    voice_name: '',
    language: 'English',
    gender: 'female',
    accent: '',
    age: '',
    emotion: 'neutral',
    pace: 'moderate',
    additional_traits: '',
    cfg_value: 2.0,
    inference_timesteps: 10
  })
  const languageLabelByCode = LANGUAGE_OPTIONS.reduce((acc, option) => {
    acc[String(option.value).toLowerCase()] = option.label
    return acc
  }, {})

  const resolvedTargetName =
    typeof targetName === 'object'
      ? targetName?.name
      : typeof targetName === 'string'
      ? targetName.trim()
      : ''
  const modalTargetName =
    resolvedTargetName || (showRecommendedTab ? 'Narrator' : '')

  const systemVoices = voices.filter(v => v.type !== 'uploaded')
  const customVoices = voices.filter(v => v.type === 'uploaded')

  const hasSystemVoices = systemVoices.length > 0
  const hasCustomVoices = customVoices.length > 0
  const hasRecommendedVoices = recommendedVoices.length > 0

  useEffect(() => {
    if (!visible) {
      setIsCreateVoiceModalVisible(false)
      setIsSynthesisSubmitting(false)
      setSynthesizedPreview(null)
      setSynthesisForm({
        voice_name: '',
        language: 'English',
        gender: 'female',
        accent: '',
        age: '',
        emotion: 'neutral',
        pace: 'moderate',
        additional_traits: '',
        cfg_value: 2.0,
        inference_timesteps: 10
      })
    }
  }, [visible])

  const updateSynthesisField = (key, value) => {
    setSynthesisForm(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSynthesizeVoice = async () => {
    if (typeof onSynthesizeVoice !== 'function') return
    if (!synthesisForm.voice_name || !synthesisForm.voice_name.trim()) {
      message.warning('Please enter a voice name')
      return
    }
    if (!synthesisForm.language || !synthesisForm.language.trim()) {
      message.warning('Please select a language')
      return
    }

    try {
      setIsSynthesisSubmitting(true)
      setSynthesizedPreview(null)
      const response = await onSynthesizeVoice({
        voice_name: synthesisForm.voice_name.trim(),
        language: synthesisForm.language.trim(),
        gender: synthesisForm.gender || 'female',
        accent: synthesisForm.accent?.trim() || '',
        age: synthesisForm.age?.trim() || '',
        emotion: synthesisForm.emotion || 'neutral',
        pace: synthesisForm.pace || 'moderate',
        additional_traits: synthesisForm.additional_traits?.trim() || '',
        cfg_value: Number(synthesisForm.cfg_value) || 2.0,
        inference_timesteps: Number(synthesisForm.inference_timesteps) || 10
      })

      const resolvedOutput =
        response?.output || response?.result || response?.data || response
      const audioUrl =
        resolvedOutput?.audio_url ||
        response?.audio_url ||
        (typeof response === 'string' ? response : '')

      if (!audioUrl) {
        message.warning('Voice generated, but no preview audio was returned.')
      }

      setSynthesizedPreview({
        audioUrl,
        description: resolvedOutput?.voice_description || '',
        sampleText: resolvedOutput?.sample_text || '',
        language: resolvedOutput?.language || synthesisForm.language,
        gender: resolvedOutput?.gender || synthesisForm.gender
      })
      message.success('Voice preview is ready')
    } catch (error) {
      message.error(error?.message || 'Failed to create voice preview')
    } finally {
      setIsSynthesisSubmitting(false)
    }
  }

  const normalizedSynthesisProgress =
    typeof synthesizeTaskProgress === 'number' &&
    !Number.isNaN(synthesizeTaskProgress)
      ? Math.max(0, Math.min(100, Math.round(synthesizeTaskProgress)))
      : 0

  const openUploadDetails = file => {
    if (setPendingUploadFile) {
      setPendingUploadFile(file)
    }
    if (!pendingVoiceName?.trim() && file) {
      setPendingVoiceName(file.name.replace(/\.[^/.]+$/, ''))
    }
    setPendingVoiceDescription('')
    setPendingVoiceLanguage('')
    setUploadDetailsModalVisible(true)
  }

  const closeUploadDetails = () => {
    setUploadDetailsModalVisible(false)
    if (setPendingUploadFile) {
      setPendingUploadFile(null)
    }
    setPendingVoiceName('')
    setPendingVoiceDescription('')
    setPendingVoiceLanguage('')
  }

  const handleFileSelect = file => {
    const fileAllowed = beforeUpload ? beforeUpload(file) : true
    if (fileAllowed !== false) {
      openUploadDetails(file)
    }
    return false
  }

  const handleConfirmUpload = async () => {
    if (!pendingUploadFile) return
    await handleUploadVoice({
      file: pendingUploadFile,
      onSuccess: () => closeUploadDetails(),
      onError: () => {}
    })
  }

  const handleRetryUploadWithClose = async () => {
    if (typeof onRetryUpload !== 'function') return
    await onRetryUpload({ onSuccess: () => closeUploadDetails() })
  }

  const handleOverwriteUploadWithClose = async () => {
    if (typeof onOverwriteUpload !== 'function') return
    await onOverwriteUpload({ onSuccess: () => closeUploadDetails() })
  }

  const normalizeLanguage = language => {
    if (typeof language !== 'string') return ''
    return language.trim()
  }

  const getVoiceLanguageLabel = voice => {
    const directLanguage = normalizeLanguage(voice?.language)
    if (directLanguage) {
      const normalized = directLanguage.toLowerCase()
      return languageLabelByCode[normalized] || directLanguage
    }

    const firstLanguageFromList = Array.isArray(voice?.languages)
      ? normalizeLanguage(voice.languages[0])
      : ''
    if (firstLanguageFromList) {
      const normalized = firstLanguageFromList.toLowerCase()
      return languageLabelByCode[normalized] || firstLanguageFromList
    }

    return 'Other Languages'
  }

  const groupVoicesByLanguage = voiceList => {
    const groupsMap = new Map()

    voiceList.forEach(voice => {
      const label = getVoiceLanguageLabel(voice)
      const key = label.toLowerCase()

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          label,
          voices: []
        })
      }

      groupsMap.get(key).voices.push(voice)
    })

    return Array.from(groupsMap.values()).sort((a, b) => {
      if (a.label === 'Other Languages') return 1
      if (b.label === 'Other Languages') return -1
      return a.label.localeCompare(b.label)
    })
  }

  const renderGroupedVoiceList = (voiceList, variant) => {
    const groupedVoices = groupVoicesByLanguage(voiceList)
    const isTableLayout = isTableLayoutVariant(variant)
    const [firstColumn, secondColumn, thirdColumn] = getTableColumns(variant)

    return (
      <>
        {voiceList.length > 0 ? (
          <div
            className={`voice-selection-modal__voice-list voice-selection-modal__voice-list--${variant} ${
              isTableLayout ? 'voice-selection-modal__voice-list--table' : ''
            } ${
              variant === 'custom' ? 'voice-selection-modal__voice-grid' : ''
            }`}
          >
            {isTableLayout && (
              <div
                className={`voice-selection-modal__table-columns voice-selection-modal__table-columns--${variant}`}
              >
                <span>{firstColumn}</span>
                <span>{secondColumn}</span>
                <span>{thirdColumn}</span>
              </div>
            )}

            {groupedVoices.map(group => (
              <section
                key={group.key}
                className={`voice-selection-modal__language-group voice-selection-modal__language-group--${variant} ${
                  isTableLayout
                    ? 'voice-selection-modal__language-group--table'
                    : ''
                }`}
              >
                <div className="voice-selection-modal__language-group-header">
                  <h4>{group.label} Voices</h4>
                  <div className="voice-selection-modal__language-group-meta">
                    <span>
                      {group.voices.length} voice
                      {group.voices.length === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>

                {!isTableLayout && (
                  <div className="voice-selection-modal__language-group-columns">
                    <span>Voice</span>
                    <span>Profile</span>
                    <span>Actions</span>
                  </div>
                )}

                <div
                  className={`voice-selection-modal__language-group-list voice-selection-modal__language-group-list--${variant}`}
                >
                  {group.voices.map((voice, index) => {
                    const displayName = getVoiceDisplayName(
                      voice,
                      variant,
                      index
                    )

                    return (
                      <VoiceListItem
                        key={voice.id}
                        voice={voice}
                        variant={variant}
                        displayName={displayName}
                        isSelected={selectedVoiceId === voice.id}
                        isPlaying={playingVoice === voice.id}
                        progress={playingVoice === voice.id ? audioProgress : 0}
                        onSelect={() => onSelect(voice.id)}
                        onViewDetails={() =>
                          setDetailsView({
                            voice,
                            variant,
                            displayName
                          })
                        }
                        onPlay={() =>
                          handlePlayPreview(voice.id, getVoicePreviewUrl(voice))
                        }
                        canDelete={
                          typeof canDeleteVoice === 'function'
                            ? canDeleteVoice(voice)
                            : false
                        }
                        onDelete={() =>
                          typeof onDeleteVoice === 'function'
                            ? onDeleteVoice(voice)
                            : null
                        }
                      />
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
            <Icon type="sound" />
            <p>No voices available for this list.</p>
          </div>
        )}
      </>
    )
  }

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={880}
      title={
        modalTargetName ? `Select Voice for ${modalTargetName}` : 'Select Voice'
      }
      centered
      className="voice-selection-modal"
      destroyOnClose
    >
      <div className="voice-selection-modal__content">
        <div className="voice-selection-modal__toolbar">
          <Button
            className="voice-selection-modal__create-btn"
            onClick={() => setIsCreateVoiceModalVisible(true)}
          >
            <Icon type="experiment" />
            Create Voice
          </Button>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} animated={false}>
          {showRecommendedTab && (
            <TabPane
              tab={
                <span>
                  <Icon type="star" />
                  Recommended Voices
                </span>
              }
              key="recommended"
            >
              <div className="voice-selection-modal__tab-content voice-selection-modal__tab-content--recommended">
                <div className="voice-selection-modal__recommendation-hero">
                  <div className="voice-selection-modal__recommendation-copy">
                    <h3>Recommended for your story</h3>
                    <p>
                      Start with the top match, compare a few options, and use
                      the eye icon to inspect each voice before choosing.
                    </p>
                  </div>
                  {typeof fetchRecommendedVoices === 'function' && (
                    <Button
                      type="default"
                      icon={hasRecommendedVoices ? 'reload' : 'bulb'}
                      onClick={fetchRecommendedVoices}
                      loading={loadingRecommendedVoices}
                      className="voice-selection-modal__refresh-btn"
                    >
                      {hasRecommendedVoices
                        ? 'Refresh'
                        : 'Generate Recommendations'}
                    </Button>
                  )}
                </div>

                {recommendedVoicesError && (
                  <Alert
                    message={recommendedVoicesError}
                    type="warning"
                    showIcon
                    className="voice-selection-modal__alert"
                  />
                )}

                {loadingRecommendedVoices ? (
                  <div className="voice-selection-modal__state-card">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Curating recommended voices...</p>
                  </div>
                ) : recommendedVoices.length > 0 ? (
                  renderGroupedVoiceList(recommendedVoices, 'recommended')
                ) : (
                  <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
                    <Icon type="bulb" />
                    <p>No recommendations available for this story yet.</p>
                  </div>
                )}
              </div>
            </TabPane>
          )}

          <TabPane
            tab={
              <span>
                <Icon type="customer-service" />
                System Voices
              </span>
            }
            key="system"
          >
            <div className="voice-selection-modal__tab-content voice-selection-modal__tab-content--system">
              <VoiceLibrarySection
                title="System voices"
                description="Choose from our library of high-quality AI voices with a cleaner, easier comparison layout."
                onRefresh={fetchVoices}
                isRefreshing={loadingVoices}
                error={voicesError}
                isLoading={loadingVoices}
                loadingText="Loading available system voices..."
                emptyIcon="customer-service"
                emptyText="No system voices are available right now."
              >
                {hasSystemVoices &&
                  renderGroupedVoiceList(systemVoices, 'system')}
              </VoiceLibrarySection>
            </div>
          </TabPane>

          <TabPane
            tab={
              <span>
                <Icon type="user" />
                Custom Voices
              </span>
            }
            key="custom"
          >
            <div className="voice-selection-modal__tab-content voice-selection-modal__tab-content--custom">
              <VoiceLibrarySection
                title="Custom voices"
                description="Upload your own sample, then compare saved custom voices using the same layout as the library."
                onRefresh={fetchVoices}
                isRefreshing={loadingVoices}
                error={voicesError}
                isLoading={false}
                emptyIcon="user"
                emptyText="No custom voices uploaded yet."
              >
                <div className="voice-selection-modal__upload-section">
                  {uploadError && !isUploadDetailsModalVisible && (
                    <Alert
                      message={uploadError}
                      type="error"
                      showIcon
                      closable
                      onClose={() => setUploadError(null)}
                      className="voice-selection-modal__alert"
                    />
                  )}

                  <Upload.Dragger
                    accept=".mp3"
                    beforeUpload={handleFileSelect}
                    showUploadList={false}
                  >
                    <p className="ant-upload-drag-icon">
                      <Icon type="cloud-upload" />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag audio file to start uploading a custom
                      voice.
                    </p>
                    <p className="ant-upload-hint">
                      Upload an MP3 file first, then add name, description, and
                      an optional language.
                    </p>
                  </Upload.Dragger>

                  <VoiceUploadDetailsModal
                    visible={isUploadDetailsModalVisible}
                    pendingUploadFile={pendingUploadFile}
                    uploadError={uploadError}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    uploadTaskProgress={uploadTaskProgress}
                    uploadTaskStatus={uploadTaskStatus}
                    uploadTaskMessage={uploadTaskMessage}
                    pendingVoiceName={pendingVoiceName}
                    pendingVoiceDescription={pendingVoiceDescription}
                    pendingVoiceLanguage={pendingVoiceLanguage}
                    setPendingVoiceName={setPendingVoiceName}
                    setPendingVoiceDescription={setPendingVoiceDescription}
                    setPendingVoiceLanguage={setPendingVoiceLanguage}
                    onCancel={closeUploadDetails}
                    onConfirmUpload={handleConfirmUpload}
                    onRetryUpload={handleRetryUploadWithClose}
                    onOverwriteUpload={handleOverwriteUploadWithClose}
                  />
                </div>

                {loadingVoices ? (
                  <div className="voice-selection-modal__state-card">
                    <div className="processing-visual">
                      <div className="custom-loader" />
                      <div className="loader-center">
                        <div className="inner-dot" />
                      </div>
                    </div>
                    <p>Loading your saved custom voices...</p>
                  </div>
                ) : hasCustomVoices ? (
                  renderGroupedVoiceList(customVoices, 'custom')
                ) : (
                  <div className="voice-selection-modal__state-card voice-selection-modal__state-card--empty">
                    <Icon type="user" />
                    <p>No custom voices uploaded yet.</p>
                  </div>
                )}
              </VoiceLibrarySection>
            </div>
          </TabPane>
        </Tabs>
      </div>

      <VoiceDetailsModal
        visible={!!detailsView}
        voice={detailsView?.voice}
        variant={detailsView?.variant}
        displayName={detailsView?.displayName}
        onClose={() => setDetailsView(null)}
      />

      <Modal
        maskClosable={!isSynthesisSubmitting}
        visible={isCreateVoiceModalVisible}
        onCancel={() => {
          if (isSynthesisSubmitting) return
          setIsCreateVoiceModalVisible(false)
        }}
        footer={null}
        width={700}
        centered
        destroyOnClose={false}
        className="voice-selection-modal__create-modal"
        title="Create Voice Preview"
      >
        <section className="voice-selection-modal__create-card">
          {!isSynthesisSubmitting ? (
            <>
              <div className="voice-selection-modal__create-head">
                <h3>Design a new voice</h3>
                <p>
                  Generate and listen to a preview here. This preview is not
                  added to voice list yet.
                </p>
              </div>

              <div className="voice-selection-modal__create-grid">
                <label className="voice-selection-modal__create-field voice-selection-modal__create-field--full">
                  <span>Voice Name</span>
                  <Input
                    value={synthesisForm.voice_name}
                    onChange={event =>
                      updateSynthesisField('voice_name', event.target.value)
                    }
                    placeholder="e.g. narrator_deep"
                  />
                </label>

                <label className="voice-selection-modal__create-field">
                  <span>Language</span>
                  <Select
                    showSearch
                    value={synthesisForm.language}
                    onChange={value => updateSynthesisField('language', value)}
                    optionFilterProp="children"
                  >
                    {SYNTHESIS_LANGUAGE_OPTIONS.map(language => (
                      <Option key={language} value={language}>
                        <span className="voice-selection-modal__option-label">
                          {formatOptionLabel(language)}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </label>

                <label className="voice-selection-modal__create-field">
                  <span>Gender</span>
                  <Select
                    value={synthesisForm.gender}
                    onChange={value => updateSynthesisField('gender', value)}
                  >
                    {GENDER_OPTIONS.map(gender => (
                      <Option key={gender} value={gender}>
                        <span className="voice-selection-modal__option-label">
                          {formatOptionLabel(gender)}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </label>

                <label className="voice-selection-modal__create-field">
                  <span>Accent</span>
                  <Input
                    value={synthesisForm.accent}
                    onChange={event =>
                      updateSynthesisField('accent', event.target.value)
                    }
                    placeholder="e.g. british, new york, parisian"
                  />
                </label>

                <label className="voice-selection-modal__create-field">
                  <span>Age</span>
                  <Select
                    showSearch
                    value={synthesisForm.age || undefined}
                    onChange={value => updateSynthesisField('age', value)}
                    placeholder="Select age profile"
                    optionFilterProp="children"
                    allowClear
                  >
                    {AGE_OPTIONS.map(age => (
                      <Option key={age} value={age}>
                        <span className="voice-selection-modal__option-label">
                          {formatOptionLabel(age)}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </label>

                <label className="voice-selection-modal__create-field">
                  <span>Emotion</span>
                  <Select
                    showSearch
                    value={synthesisForm.emotion}
                    onChange={value => updateSynthesisField('emotion', value)}
                    optionFilterProp="children"
                  >
                    {EMOTION_OPTIONS.map(emotion => (
                      <Option key={emotion} value={emotion}>
                        <span className="voice-selection-modal__option-label">
                          {formatOptionLabel(emotion)}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </label>

                <label className="voice-selection-modal__create-field">
                  <span>Pace</span>
                  <Select
                    showSearch
                    value={synthesisForm.pace}
                    onChange={value => updateSynthesisField('pace', value)}
                    optionFilterProp="children"
                  >
                    {PACE_OPTIONS.map(pace => (
                      <Option key={pace} value={pace}>
                        <span className="voice-selection-modal__option-label">
                          {formatOptionLabel(pace)}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </label>

                <label className="voice-selection-modal__create-field voice-selection-modal__create-field--full">
                  <span>Additional Traits</span>
                  <Input
                    value={synthesisForm.additional_traits}
                    onChange={event =>
                      updateSynthesisField(
                        'additional_traits',
                        event.target.value
                      )
                    }
                    placeholder="e.g. smooth, deep, conversational"
                  />
                </label>
              </div>

              <div className="voice-selection-modal__create-grid voice-selection-modal__create-grid--quality">
                <label className="voice-selection-modal__create-field">
                  <span>
                    CFG Value
                    <Tooltip title="Expressiveness control (0.5 to 5.0).">
                      <Icon type="question-circle" />
                    </Tooltip>
                  </span>
                  <InputNumber
                    min={0.5}
                    max={5}
                    step={0.1}
                    value={synthesisForm.cfg_value}
                    onChange={value =>
                      updateSynthesisField('cfg_value', value || 2.0)
                    }
                  />
                </label>

                <label className="voice-selection-modal__create-field">
                  <span>
                    Inference Timesteps
                    <Tooltip title="Quality setting (1 to 50). Higher is slower and often better quality.">
                      <Icon type="question-circle" />
                    </Tooltip>
                  </span>
                  <InputNumber
                    min={1}
                    max={50}
                    step={1}
                    value={synthesisForm.inference_timesteps}
                    onChange={value =>
                      updateSynthesisField('inference_timesteps', value || 10)
                    }
                  />
                </label>
              </div>

              <div className="voice-selection-modal__create-actions">
                <Button
                  className="voice-selection-modal__generate-btn"
                  onClick={handleSynthesizeVoice}
                >
                  Generate
                </Button>
              </div>
            </>
          ) : (
            <div className="voice-selection-modal__generating-state">
              <div className="voice-selection-modal__generating-icon">
                <Icon type="loading" />
              </div>
              <h4>Generating Voice Preview</h4>
              <p>{synthesizeTaskMessage || 'Creating your voice sample...'}</p>
              <Progress
                percent={normalizedSynthesisProgress}
                status={
                  synthesizeTaskStatus === 'failed' ? 'exception' : 'active'
                }
              />
            </div>
          )}

          {synthesizedPreview?.audioUrl && (
            <div className="voice-selection-modal__preview-card">
              <div className="voice-selection-modal__preview-meta">
                <span>{formatOptionLabel(synthesizedPreview.language)}</span>
                <span>{formatOptionLabel(synthesizedPreview.gender)}</span>
              </div>
              {synthesizedPreview.description && (
                <p>{synthesizedPreview.description}</p>
              )}
              {synthesizedPreview.sampleText && (
                <p className="voice-selection-modal__preview-sample">
                  {synthesizedPreview.sampleText}
                </p>
              )}
              <audio controls src={synthesizedPreview.audioUrl} />
            </div>
          )}
        </section>
      </Modal>
    </Modal>
  )
}

export default VoiceSelectionModal
